// Tenant ID lookup table by namespace and environment
export const TENANT_IDS: Record<string, Record<string, number>> = {
  cnx: { development: 997, staging: 998, production: 999 },
  frg: { development: 897, staging: 898, production: 899 },
}

// Environments available for selection, in display order.
export const REMA_ENVIRONMENTS = ['development', 'staging', 'production'] as const
export type RemaEnvironment = (typeof REMA_ENVIRONMENTS)[number]

export function getTenantId(namespace: string, env: string): number {
  return TENANT_IDS[namespace]?.[env] ?? 997
}

interface BuildResourceTypeUriParams {
  namespace: string
  tenantId: number | string
  category: string
  parentNames: string[]
  resourceTypeName: string
}

export function buildResourceTypeUri({
  namespace,
  tenantId,
  category,
  parentNames,
  resourceTypeName,
}: BuildResourceTypeUriParams): string {
  if (!namespace || !category || !resourceTypeName) return ''
  const parts = [category, ...parentNames, resourceTypeName].filter(Boolean)
  return `${namespace}://${tenantId}/${parts.join('/')}`
}

interface BuildResourceInstanceUriParams {
  namespace: string
  tenantId: number | string
  category: string
  parentInstancePaths: { resourceType: string; instanceId: string }[]
  resourceType: string
  instanceName: string
}

export function buildResourceInstanceUri({
  namespace,
  tenantId,
  category,
  parentInstancePaths,
  resourceType,
  instanceName,
}: BuildResourceInstanceUriParams): string {
  if (!namespace || !category || !resourceType || !instanceName) return ''
  const parentSegments = parentInstancePaths
    .flatMap(({ resourceType: rt, instanceId }) => [rt, instanceId])
    .filter(Boolean)
  const parts = [category, ...parentSegments, resourceType, instanceName]
  return `${namespace}://${tenantId}/${parts.join('/')}`
}

interface BuildRoleUriParams {
  namespace: string
  roleType: string
  organization: string
  roleName: string
}

// Normalize a display value (e.g. an Organization Type like "Robot Manufacturer")
// to a lower kebab-case URI segment ("robot-manufacturer") per the URI convention.
export function toKebabCase(value: string): string {
  return value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

export function buildRoleUri({ namespace, roleType, organization, roleName }: BuildRoleUriParams): string {
  if (!namespace || !roleType || !roleName) return ''
  // Workload roles are not scoped to an organization — no org segment in the URI.
  if (roleType === 'workload') {
    return `${namespace}://role/workload/${roleName}`
  }
  // User roles include the organization (kebab-cased; the form shows the friendly label).
  if (!organization) return ''
  return `${namespace}://role/${roleType}/${toKebabCase(organization)}/${roleName}`
}

interface BuildGroupUriParams {
  namespace: string
  organization: string
  groupName: string
}

export function buildGroupUri({ namespace, organization, groupName }: BuildGroupUriParams): string {
  if (!namespace || !organization || !groupName) return ''
  return `${namespace}://group/${organization}/${groupName}`
}
